import { memo } from 'react';
import { TextField } from '../../components/fields/TextField';
import { NumberField } from '../../components/fields/NumberField';
import { SelectField } from '../../components/fields/SelectField';
import { MultiSelectField } from '../../components/fields/MultiSelectField';
import { BooleanField } from '../../components/fields/BooleanField';
import { DateField } from '../../components/fields/DateField';
import { ImageField } from '../../components/fields/ImageField';
import { AddressField } from '../../components/fields/AddressField';
import { StatusField } from '../../components/fields/StatusField';
import { LookupField } from '../../components/fields/LookupField';
import { RadioField } from '../../components/fields/RadioField';
import { GridField } from '../../components/fields/GridField';

export const AttributeRenderer = memo(function AttributeRenderer({ attribute, value, error, isRequired, isEditable, onChange }) {
  const common = {
    id: attribute.id,
    label: attribute.label,
    error,
    helpText: attribute.helpText,
    required: isRequired,
    readOnly: !isEditable,
    placeholder: attribute.placeholder,
    unit: attribute.unit,
    currency: attribute.currency,
    decimalPlaces: attribute.decimalPlaces,
  };

  switch (attribute.type) {
    case 'text': case 'barcode': case 'qr-code':
      return <TextField {...common} value={value ?? ''} onChange={v => onChange(v)} maxLength={attribute.validation?.maxLength} />;

    case 'password':
      return <TextField {...common} value={value ?? ''} onChange={v => onChange(v)} type="password" />;

    case 'textarea':
      return <TextField {...common} value={value ?? ''} onChange={v => onChange(v)} maxLength={attribute.validation?.maxLength} multiline />;

    case 'number': case 'decimal': case 'currency': case 'calculated': case 'formula':
      return <NumberField {...common} value={value === undefined || value === '' ? '' : Number(value)} onChange={v => onChange(v)}
        readOnly={!isEditable || attribute.type === 'calculated' || attribute.type === 'formula'}
        min={attribute.validation?.min} max={attribute.validation?.max} />;

    case 'dropdown': case 'tax-selector':
      return <SelectField {...common} value={value ?? ''} onChange={v => onChange(v)} options={attribute.options ?? []} />;

    case 'multi-select':
      return <MultiSelectField {...common} value={value ?? []} onChange={v => onChange(v)} options={attribute.options ?? []} />;

    case 'radio': case 'workflow':
      return <RadioField {...common} value={value ?? ''} onChange={v => onChange(v)} options={attribute.options ?? []} />;

    case 'boolean': case 'checkbox':
      return <BooleanField {...common} value={Boolean(value ?? attribute.defaultValue ?? false)} onChange={v => onChange(v)} />;

    case 'date': case 'datetime':
      return <DateField {...common} value={value ?? ''} onChange={v => onChange(v)}
        type={attribute.type === 'datetime' ? 'datetime-local' : 'date'}
        min={attribute.validation?.futureDate ? new Date().toISOString().split('T')[0] : undefined} />;

    case 'time':
      return <DateField {...common} value={value ?? ''} onChange={v => onChange(v)} type="time" />;

    case 'image-url': case 'file-upload':
      return <ImageField {...common} value={value ?? (attribute.allowMultiple ? [] : '')} onChange={v => onChange(v)} allowMultiple={attribute.allowMultiple} />;

    case 'lookup': case 'autocomplete': case 'entity-picker':
      return <LookupField {...common} value={value ?? ''} onChange={v => onChange(v)} options={attribute.options ?? []} />;

    case 'grid':
      return <GridField {...common} value={value ?? []} onChange={v => onChange(v)} columns={attribute.gridColumns ?? []} />;

    case 'address':
      return <AddressField {...common} value={value} onChange={v => onChange(v)} />;

    case 'status':
      return <StatusField {...common} value={value ?? ''} onChange={v => onChange(v)} options={attribute.options ?? []} />;

    default:
      return <div style={{ color:'red', fontSize:'0.8rem' }}>Unknown field type: {attribute.type}</div>;
  }
}, (prev, next) =>
  prev.value === next.value && prev.error === next.error &&
  prev.isRequired === next.isRequired && prev.isEditable === next.isEditable
);
